/**
 * The board (Miro's): every node an item placed freely at its own size (`node.board`), with
 * its own look (`node.style`), and frames (groups with a `board` rectangle) carrying what is
 * inside them. This service keeps what must outlive the graph window: the board's pan and zoom,
 * the tool chosen, the gesture in progress and what others drag, and every edit the board makes.
 *
 * A gesture (move, resize, rotate) is shown at once and to everyone a few times per second
 * (`project.drag_boxes` with `view: "board"`, nothing stored); its end is **one** `graph.batch`
 * of `update_node { board }` / `update_group { board }` (one undo), which also puts a dropped
 * item into the frame it landed in. Everything else (new items, styles, groups, duplicates,
 * pastes, z-order, alignment) is one batch too.
 *
 * This file only wires the service together: each concern is a part of its own (`camera.ts`,
 * `tools.ts`, `selection.ts`, `moves.ts`, `ink.ts`, `crop.ts`, `create.ts`, `words.ts`,
 * `arrange.ts`, `clipboard.ts`), sharing what `context.ts` builds. What a GUI pack adds to the
 * board (UI/UX's screens, components, prototype mode) is the pack's (`packs/<id>/`, `state/packs.ts`).
 */
import { arrangePart } from "./arrange";
import { cameraPart } from "./camera";
import { clipboardPart } from "./clipboard";
import { createBoardContext } from "./context";
import { createPart } from "./create";
import { cropPart } from "./crop";
import { inkPart } from "./ink";
import { movesPart } from "./moves";
import { selectionPart } from "./selection";
import { followOthers } from "./sync";
import { toolsPart } from "./tools";
import type { Board, BoardDeps } from "./types";
import { wordsPart } from "./words";

export * from "./geometry";
export * from "./constants";
export * from "./types";
export { kindTools, OWN_TOOLS } from "./tools";
export { boardCommands, boardMenus } from "./commands";

/** The board service, built from its parts. */
export function createBoard(deps: BoardDeps): Board {
  const ctx = createBoardContext(deps);
  const board = Object.assign(
    {} as Board,
    cameraPart(ctx),
    toolsPart(ctx),
    selectionPart(ctx),
    movesPart(ctx),
    inkPart(ctx),
    cropPart(ctx),
    createPart(ctx),
    wordsPart(ctx),
    arrangePart(ctx),
    clipboardPart(ctx),
  );
  ctx.board = board;
  followOthers(ctx);
  return board;
}
